import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_APP_CONTEXT } from '../constants';
import { CurrentUserSite, CustomerUserAccountType } from '../graphql/generated';
import {
  AppContextType,
  ContractCreationBlockedReason,
  CurrentUser,
  ModuleName,
  OrderFlow,
  PageBackground,
  TFormValues,
} from '../types';
import {
  businessAccount,
  completeAddress,
  getDefaultSite,
  getFullName,
  getSelectedSiteFromUrl,
  getValidDeliveryAddress,
} from '../utils/utils';

export const AppContext = createContext<AppContextType>(DEFAULT_APP_CONTEXT);

export const useAppContext = () => {
  const context = use(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within a AppContextProvider');
  }
  return context;
};

type AppContextProviderProps = {
  initialValues?: AppContextType;
  children: React.ReactNode;
};

export const AppContextProvider = ({
  children,
  initialValues = DEFAULT_APP_CONTEXT,
}: AppContextProviderProps) => {
  const [user, setUser] = useState<CurrentUser | null>(initialValues.user);
  const [orderFlow, setOrderFlow] = useState<OrderFlow | null>(initialValues.orderFlow || null);
  const [currentModal, setCurrentModal] = useState<ReactNode>();

  const [userProcessing, setUserProcessing] = useState<boolean>(initialValues.userProcessing);
  const [formValues, setFormValues] = useState<TFormValues>(initialValues.formValues);
  const [orderComplete, setOrderComplete] = useState<boolean>(initialValues.orderComplete);
  const [customBillingAddressState, setCustomBillingAddressState] = useState<boolean>(
    initialValues.customBillingAddress,
  );
  const [selectedSite, setSelectedSite] = useState<CurrentUserSite | null>(
    initialValues.selectedSite,
  );
  const [contractCreationBlocked, setContractCreationBlocked] =
    useState<ContractCreationBlockedReason | null>(null);
  const [offerRequiresManualAddress, setOfferRequiresManualAddress] = useState<boolean>(
    initialValues.offerRequiresManualAddress,
  );
  const [accountType, setAccountType] = useState<CustomerUserAccountType>(
    CustomerUserAccountType.PrivateCustomer,
  );
  const [currentBackground, setCurrentBackground] = useState<PageBackground | undefined>();
  const [showVisitorForm, setShowVisitorForm] = useState<boolean>(false);

  const [modules, setModules] = useState<Set<ModuleName>>(new Set(initialValues.modules));
  const [dismissedModules, setDismissedModules] = useState<Set<ModuleName>>(new Set());

  const toggleModule = (module: ModuleName) => {
    const newModules = new Set(modules);
    const newDismissed = new Set(dismissedModules);
    if (modules.has(module)) {
      newModules.delete(module);
      newDismissed.add(module);
    } else {
      newModules.add(module);
      newDismissed.delete(module);
    }
    setModules(newModules);
    setDismissedModules(newDismissed);
  };

  const addModule = useCallback(
    (module: ModuleName) => {
      if (dismissedModules.has(module)) return;

      setModules((previousModules) => {
        if (previousModules.has(module)) return previousModules;
        const newModules = new Set(previousModules);
        newModules.add(module);
        return newModules;
      });
    },
    [dismissedModules],
  );

  const removeModule = (module: ModuleName) => {
    const newModules = new Set(modules);
    newModules.delete(module);
    setModules(newModules);
  };

  const resetOrderState = useCallback(() => {
    setOrderComplete(false);
    setModules(new Set());
    setDismissedModules(new Set());
    setCurrentBackground(undefined);
    setOrderFlow(null);
    setCustomBillingAddressState(false);
    setOfferRequiresManualAddress(false);
    setShowVisitorForm(false);
    setContractCreationBlocked(null);
    setFormValues((prev) => ({
      ...prev,
      iban: '',
      meterId: '',
      marketLocationId: '',
      paymentAuthorization: false,
      termsAndConditions: false,
      bindingContract: false,
      marketing: false,
      interestedInHardwareOffer: false,
      contractTerminated: false,
      confirmedCancellationDate: undefined,
      terminatedEnergyProviderName: undefined,
      terminatedEnergyProviderId: undefined,
      providerChangeReason: undefined,
      personalData: {
        ...prev.personalData,
        taxId: undefined,
        taxStatus: undefined,
      },
    }));
  }, []);

  const setCustomBillingAddress = (value: boolean) => {
    if (value && Object.entries(formValues.billingAddress).length === 0) {
      setFormValues({
        ...formValues,
        billingAddress: completeAddress(formValues.deliveryAddress),
      });
    }
    setCustomBillingAddressState(value);
  };

  useEffect(() => {
    if (!user) return;

    setAccountType(user.account.type);

    const selectedSiteFromUrl = getSelectedSiteFromUrl(user);
    const initialSelectedSite = selectedSiteFromUrl || getDefaultSite(user);

    const newFormValues = {
      ...formValues,
      accountOwner: getFullName(user?.contact),
      personalData: {
        ...formValues.personalData,
        firstName: user.contact.firstName ?? '',
        lastName: user.contact.lastName ?? '',
        email: user.contact.email ?? '',
        phone: user.contact.phone ?? '',
      },
      deliveryAddress: getValidDeliveryAddress(user, initialSelectedSite),
      billingAddress: {
        postalCode: user.account.billingAddress.postalCode ?? '',
        city: user.account.billingAddress.city ?? '',
        streetName: user.account.billingAddress.streetName ?? '',
        streetNumber: user.account.billingAddress.streetNumber ?? '',
        country: user.account.billingAddress.country ?? '',
      },
    };

    if (businessAccount(user.account.type))
      newFormValues.personalData.companyName = user.account.companyName ?? '';

    setFormValues(newFormValues);

    if (initialSelectedSite) setSelectedSite(initialSelectedSite);
  }, [user]);

  const value: AppContextType = useMemo(() => {
    return {
      accountType,
      addModule,
      currentBackground,
      setCurrentBackground,
      formValues,
      modules,
      orderFlow,
      orderComplete,
      offerRequiresManualAddress,
      contractCreationBlocked,
      setContractCreationBlocked,
      removeModule,
      resetOrderState,
      selectedSite,
      setAccountType,
      setFormValues,
      setModules,
      setOfferRequiresManualAddress,
      setOrderComplete,
      setSelectedSite,
      setUser,
      setUserProcessing,
      toggleModule,
      user,
      userProcessing,
      customBillingAddress: customBillingAddressState,
      setCustomBillingAddress,
      setOrderFlow,
      currentModal,
      setCurrentModal,
      showVisitorForm,
      setShowVisitorForm,
    };
  }, [
    accountType,
    addModule,
    formValues,
    modules,
    offerRequiresManualAddress,
    contractCreationBlocked,
    orderComplete,
    orderFlow,
    removeModule,
    resetOrderState,
    selectedSite,
    setAccountType,
    setModules,
    setFormValues,
    setOfferRequiresManualAddress,
    setContractCreationBlocked,
    setOrderComplete,
    setSelectedSite,
    setUser,
    setUserProcessing,
    user,
    userProcessing,
    customBillingAddressState,
    setCustomBillingAddress,
    setOrderFlow,
    currentModal,
    setCurrentModal,
    showVisitorForm,
    setShowVisitorForm,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
